import React, { useState } from 'react';
import { Card } from '../../SubscriptionAppCard';
import { Plus, Minus, AlertCircle, Lock, CheckCircle } from 'lucide-react';
interface ProductSelectorProps {
  isEditing: boolean;
  formData: any;
  setFormData: (data: any) => void;
  selectedProducts: Record<string, number>;
  updateProductQuantity: (productId: string, delta: number) => void;
  availableProducts: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  currentTotalBottles: number;
  targetBottles: number;
  isBottleCountCorrect: boolean;
  subscriptionItems: Array<{
    name: string;
    quantity: number;
  }>;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
  subscriptionTitle: string;
  'data-id'?: string;
}
// Mock fixed box definitions (in real app, this would come from Club Builder)
const FIXED_BOXES = [{
  id: 'premium-red',
  name: 'Premium Red Club',
  bottleCount: '6',
  frequency: 'Every 2 Months',
  wines: [{
    name: '2019 Penfolds Bin 389',
    quantity: 2,
    price: 89.99
  }, {
    name: '2020 Henschke Mount Edelstone',
    quantity: 2,
    price: 125.0
  }, {
    name: '2018 Torbreck The Struie',
    quantity: 2,
    price: 65.0
  }]
}, {
  id: 'mixed-dozen',
  name: 'Mixed Dozen',
  bottleCount: '12',
  frequency: 'Every 3 Months',
  wines: [{
    name: '2021 Shaw + Smith Sauvignon Blanc',
    quantity: 3,
    price: 32.0
  }, {
    name: '2020 Penfolds Bin 28',
    quantity: 3,
    price: 45.0
  }, {
    name: '2019 Yalumba The Octavius',
    quantity: 3,
    price: 95.0
  }, {
    name: '2020 Leeuwin Estate Art Series Chardonnay',
    quantity: 3,
    price: 78.0
  }]
}, {
  id: 'cellar-selection',
  name: 'Cellar Selection',
  bottleCount: '6',
  frequency: 'Every 6 Months',
  wines: [{
    name: '2015 Penfolds Grange',
    quantity: 2,
    price: 850.0
  }, {
    name: '2016 Henschke Hill of Grace',
    quantity: 2,
    price: 795.0
  }, {
    name: '2017 Cullen Diana Madeline',
    quantity: 2,
    price: 165.0
  }]
}, {
  id: 'sparkling-club',
  name: 'Sparkling Club',
  bottleCount: '6',
  frequency: 'Every Month',
  wines: [{
    name: 'NV Chandon Brut',
    quantity: 3,
    price: 28.0
  }, {
    name: 'NV Jansz Premium Cuvée',
    quantity: 3,
    price: 35.0
  }]
}];
export function ProductSelector({
  isEditing,
  formData,
  setFormData,
  selectedProducts,
  updateProductQuantity,
  availableProducts,
  currentTotalBottles,
  targetBottles,
  isBottleCountCorrect,
  subscriptionItems,
  subtotal,
  discountPercent,
  discountAmount,
  total,
  subscriptionTitle,
  'data-id': dataId
}: ProductSelectorProps) {
  const [selectedFixedBox, setSelectedFixedBox] = useState<string>('');
  // Helper function to get product price by name
  const getProductPrice = (productName: string): number => {
    // Try exact match first
    const exactMatch = availableProducts.find(p => p.name === productName);
    if (exactMatch) return exactMatch.price;
    // Try partial match (subscription items may have SKU prefix like '25FID6 – 2025 Fiano')
    const partialMatch = availableProducts.find(p => productName.includes(p.name) || p.name.includes(productName));
    if (partialMatch) return partialMatch.price;
    // Fallback: keyword-based matching
    if (productName.includes('Fiano')) return 35.0;
    if (productName.includes('Shiraz')) return 45.0;
    if (productName.includes('Chardonnay')) return 40.0;
    if (productName.includes('Pinot Noir')) return 42.0;
    if (productName.includes('Sauvignon Blanc')) return 38.0;
    if (productName.includes('Cabernet Sauvignon')) return 55.0;
    if (productName.includes('Rosé') || productName.includes('Rose')) return 32.0;
    if (productName.includes('Tempranillo')) return 36.0;
    if (productName.includes('Pinot Grigio')) return 34.0;
    if (productName.includes('Viognier')) return 39.0;
    if (productName.includes('Malbec')) return 44.0;
    if (productName.includes('GSM')) return 42.0;
    return 38.0;
  };
  // Tax rate (10% GST)
  const TAX_RATE = 0.1;
  // Calculate tax and price minus tax
  const calculatePricing = (price: number, quantity: number) => {
    const totalPrice = price * quantity;
    const priceMinusTax = totalPrice / (1 + TAX_RATE);
    const tax = totalPrice - priceMinusTax;
    return {
      totalPrice,
      priceMinusTax,
      tax
    };
  };
  // Calculate total tax for all items
  const calculateTotalTax = () => {
    return subscriptionItems.reduce((totalTax, item) => {
      const price = getProductPrice(item.name);
      const pricing = calculatePricing(price, item.quantity);
      return totalTax + pricing.tax;
    }, 0);
  };
  const totalTax = calculateTotalTax();
  const subtotalExTax = subtotal / (1 + TAX_RATE);
  // Get selected fixed box data
  const fixedBoxData = FIXED_BOXES.find(box => box.id === selectedFixedBox);
  // Handle preference change
  const handlePreferenceChange = (newPreference: string) => {
    if (formData.preference === 'Custom' && newPreference === 'Fixed') {
      // Switching to Fixed - clear custom selection
      setFormData({
        ...formData,
        preference: newPreference
      });
      setSelectedFixedBox('');
    } else if (formData.preference === 'Fixed' && newPreference === 'Custom') {
      // Switching back to Custom - optionally show confirmation
      const confirmSwitch = window.confirm('Switching to custom will clear current selection. Continue?');
      if (confirmSwitch) {
        setFormData({
          ...formData,
          preference: newPreference
        });
        setSelectedFixedBox('');
      }
    } else {
      setFormData({
        ...formData,
        preference: newPreference
      });
    }
  };
  // Handle fixed box selection
  const handleFixedBoxChange = (boxId: string) => {
    setSelectedFixedBox(boxId);
    const box = FIXED_BOXES.find(b => b.id === boxId);
    if (box) {
      setFormData({
        ...formData,
        bottleCount: box.bottleCount,
        frequency: box.frequency
      });
    }
  };
  const isFixedMode = formData.preference === 'Fixed';
  const isFixedBoxSelected = isFixedMode && selectedFixedBox && fixedBoxData;
  return <Card padding="none" data-id="element-5316" data-mp="spwfuaifnlal">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-5317" data-mp="ex6orro1rq6m">
        <div className="flex items-center justify-between" data-id="element-5318" data-mp="iwxbnkagdm5v">
          <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-5319" data-mp="4y7ds2ft7igg">
            Current Selection
          </h3>
          <div className="bg-[#E0F2FE] text-[#0EA5E9] px-4 py-2 rounded-md text-[13px] font-medium" data-id="element-5320" data-mp="9x0vy861jxmy">
            {isFixedBoxSelected ? `${fixedBoxData.bottleCount} Bottles ${fixedBoxData.frequency}` : `Choose Your Own - ${subscriptionTitle}`}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 bg-white" data-id="element-5321" data-mp="1qb0rz81rn34x">
        {isEditing ? <div className="space-y-6" data-id="element-5322" data-mp="1qcbs8xw05sra">
            {/* Settings Row - Preference, Club, Bottle Count, Frequency */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5323" data-mp="1uat3ntmghpei">
              {/* Preference Dropdown */}
              <div data-id="element-5324" data-mp="3atuos55voveu">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5325" data-mp="3aumcyux5bqv3">
                  Preference
                </label>
                <select className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat hover:border-[#94A3B8] cursor-pointer" value={formData.preference} onChange={e => handlePreferenceChange(e.target.value)} data-id="element-5326" data-mp="3on2j44lcg508">
                  <option value="Custom" data-id="element-5327" data-mp="1rbvihtec1h7m">Custom</option>
                  <option value="Fixed" data-id="element-5328" data-mp="1rakh208d0ljo">Fixed</option>
                </select>
              </div>

              {/* Fixed Box Selector - Only show when Fixed preference is selected */}
              {isFixedMode && <div data-id="element-5329" data-mp="214b02plpw3rz">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5330" data-mp="14ch6zhy7jf5u">
                    Select Club
                  </label>
                  <select className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat hover:border-[#94A3B8] cursor-pointer" value={selectedFixedBox} onChange={e => handleFixedBoxChange(e.target.value)} data-id="element-5331" data-mp="18eb2lbtjpsz5">
                    <option value="" data-id="element-5332" data-mp="3vif3knr6arm0">Choose a club...</option>
                    {FIXED_BOXES.map(box => <option key={box.id} value={box.id} data-id="element-5333" data-mp="3vgc65inoxw79">
                        {box.name}
                      </option>)}
                  </select>
                </div>}

              {/* Bottle Count Dropdown */}
              <div data-id="element-5334" data-mp="3glo30b5rj9ut">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5 flex items-center gap-2" data-id="element-5335" data-mp="3gm7mvxez48bh">
                  Bottle Count
                  {isFixedBoxSelected && <Lock className="w-3 h-3 text-[#94A3B8]" data-id="element-5336" data-mp="1eu1hgp4n475q" />}
                </label>
                <select disabled={isFixedBoxSelected} className={`w-full h-10 px-3 pr-10 border rounded-md text-[14px] outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat ${isFixedBoxSelected ? 'bg-[#F1F5F9] border-[#E2E8F0] text-[#64748B] cursor-not-allowed' : 'bg-white border-[#CBD5E1] text-[#0F172A] hover:border-[#94A3B8] cursor-pointer focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20'}`} value={formData.bottleCount} onChange={e => setFormData({
              ...formData,
              bottleCount: e.target.value
            })} data-id="element-5337" data-mp="1es8af8q47kx7">
                  <option value="3" data-id="element-5338" data-mp="2kmncu012qmdf">3 Bottles</option>
                  <option value="6" data-id="element-5339" data-mp="2kl2j2cml37gm">6 Bottles</option>
                  <option value="12" data-id="element-5340" data-mp="2pe5e6p70eoyx">12 Bottles</option>
                </select>
                {isFixedBoxSelected && <p className="text-[12px] text-[#64748B] mt-1" data-id="element-5341" data-mp="2apr5hxmww6ag">
                    Defined by club settings
                  </p>}
              </div>

              {/* Frequency Dropdown */}
              <div data-id="element-5342" data-mp="33ddghqygyd7c">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5 flex items-center gap-2" data-id="element-5343" data-mp="33eyanfgvfu4o">
                  Frequency
                  {isFixedBoxSelected && <Lock className="w-3 h-3 text-[#94A3B8]" data-id="element-5344" data-mp="2udi119xvrw1i" />}
                </label>
                <select disabled={isFixedBoxSelected} className={`w-full h-10 px-3 pr-10 border rounded-md text-[14px] outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat ${isFixedBoxSelected ? 'bg-[#F1F5F9] border-[#E2E8F0] text-[#64748B] cursor-not-allowed' : 'bg-white border-[#CBD5E1] text-[#0F172A] hover:border-[#94A3B8] cursor-pointer focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20'}`} value={formData.frequency} onChange={e => setFormData({
              ...formData,
              frequency: e.target.value
            })} data-id="element-5345" data-mp="2yek1tv7g4g5l">
                  <option value="Every Month" data-id="element-5346" data-mp="2rz3h8hofivai">Every Month</option>
                  <option value="Every 2 Months" data-id="element-5347" data-mp="v61k7xt74nu1">Every 2 Months</option>
                  <option value="Every 3 Months" data-id="element-5348" data-mp="v58lnk4e54mz">Every 3 Months</option>
                  <option value="Every 6 Months" data-id="element-5349" data-mp="q88h5dn6w9xz">Every 6 Months</option>
                </select>
                {isFixedBoxSelected && <p className="text-[12px] text-[#64748B] mt-1" data-id="element-5350" data-mp="ynpavp2hle0z">
                    Defined by club settings
                  </p>}
              </div>
            </div>

            {/* Validation/Confirmation Alert */}
            {isFixedBoxSelected ?
        // Fixed Box Confirmation
        <div className="p-4 rounded-md border bg-[#D1FAE5] border-[#10B981] transition-all duration-200" data-id="element-5351" data-mp="1ce17o1a0o0yg">
                  <div className="flex items-start gap-3" data-id="element-5352" data-mp="fqwg1odo854j">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#10B981]" data-id="element-5353" data-mp="jpwyv6w5f7rj" />
                    <div data-id="element-5354" data-mp="op8gsj894jap">
                      <p className="text-[14px] font-semibold text-[#059669]" data-id="element-5355" data-mp="op04otjydi0k">
                        Club selection applied
                      </p>
                      <p className="text-[13px] mt-1 text-[#047857]" data-id="element-5356" data-mp="204x1f3y1968c">
                        {fixedBoxData.bottleCount} bottles{' '}
                        {fixedBoxData.frequency.toLowerCase()}
                      </p>
                    </div>
                  </div>
                </div> :
        // Custom Bottle Counter Alert
        <div className={`p-4 rounded-md border transition-all duration-200 ${isBottleCountCorrect ? 'bg-[#D1FAE5] border-[#10B981]' : 'bg-[#FEF3C7] border-[#F59E0B]'}`} data-id="element-5357" data-mp="392n3h0ep2j8z">
                  <div className="flex items-start gap-3" data-id="element-5358" data-mp="3i2tjasxv7vbb">
                    <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isBottleCountCorrect ? 'text-[#10B981]' : 'text-[#F59E0B]'}`} data-id="element-5359" data-mp="3i18nz13q88ce" />
                    <div data-id="element-5360" data-mp="3s198gqdc0ui6">
                      <p className={`text-[14px] font-semibold ${isBottleCountCorrect ? 'text-[#059669]' : 'text-[#D97706]'}`} data-id="element-5361" data-mp="3rzoaeqrt6tfk">
                        {isBottleCountCorrect ? 'Bottle count is correct' : 'Adjust bottle selection'}
                      </p>
                      <p className={`text-[13px] mt-1 ${isBottleCountCorrect ? 'text-[#047857]' : 'text-[#B45309]'}`} data-id="element-5362" data-mp="jkxoouxx84jp">
                        {currentTotalBottles} of {targetBottles} bottles
                        selected
                      </p>
                    </div>
                  </div>
                </div>}

            {/* Wine List - full width, below the settings */}
            <div data-id="element-5363" data-mp="134mzpn7skcol">
              <label className="block text-[12px] font-medium text-[#475569] mb-3" data-id="element-5364" data-mp="o9gsiwwym7to">
                {isFixedBoxSelected ? 'Included Wines' : 'Select Wines'}
              </label>
              <div className={`border border-[#CBD5E1] rounded-md max-h-[420px] overflow-y-auto shadow-sm ${isFixedBoxSelected ? 'bg-[#F8FAFC]' : 'bg-white'}`} data-id="element-5365" data-mp="tammsz69q35b">
                {isFixedBoxSelected ?
            // Fixed Box - Read-only wine list
            <>
                    {fixedBoxData.wines.map((wine, index) => <div key={index} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 bg-[#F8FAFC]" data-id="element-5366" data-mp="5h4jxsfpqab3" data-mpi={index}>
                        <div className="flex-1 min-w-0" data-id="element-5367" data-mp="3soyff9gya8n4">
                          <p className="text-[14px] font-medium text-[#0F172A] break-words" data-id="element-5368" data-mp="jmien40yxd4">
                            {wine.name}
                          </p>
                          <p className="text-[13px] text-[#94A3B8] mt-0.5" data-id="element-5369" data-mp="237un20n7bdr2">
                            ${wine.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center" data-id="element-5370" data-mp="1th9lq5g57v4g">
                          <span className="inline-flex items-center justify-center min-w-[48px] h-8 px-3 bg-[#E2E8F0] text-[#475569] font-semibold rounded-md text-[14px]" data-id="element-5371" data-mp="1oi5dpi4yg3fp">
                            ×{wine.quantity}
                          </span>
                        </div>
                      </div>)}
                    <div className="px-4 py-3 bg-[#F1F5F9] border-t border-[#E2E8F0]" data-id="element-5372" data-mp="2c6gaa93mbgy6">
                      <p className="text-[12px] text-[#64748B] flex items-center gap-2" data-id="element-5373" data-mp="2865vq81rfi7p">
                        <Lock className="w-3 h-3" data-id="element-5374" data-mp="10rt1vw6l9mju" />
                        Wine selection is managed by club settings
                      </p>
                    </div>
                  </> :
            // Custom - Editable wine list
            availableProducts.map(product => {
              const quantity = selectedProducts[product.id] || 0;
              const isSelected = quantity > 0;
              return <div key={product.id} className={`flex items-center justify-between gap-4 px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 transition-all duration-200 ${isSelected ? 'bg-[#E0F2FE]' : 'bg-white hover:bg-[#F8FAFC]'}`} data-id="element-5375" data-mp="3her7j0fpgmrf">
                        <div className="flex-1 min-w-0" data-id="element-5376" data-mp="3r3iz37gut51t">
                          <p className="text-[14px] font-medium text-[#0F172A] break-words" data-id="element-5377" data-mp="3w35adbd4gb7z">
                            {product.name}
                          </p>
                          <p className="text-[13px] text-[#94A3B8] mt-0.5" data-id="element-5378" data-mp="3xi4sq7gmyqe">
                            ${product.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-5379" data-mp="25ssj3967ejt5">
                          <button onClick={() => updateProductQuantity(product.id, -1)} disabled={quantity === 0} className="w-10 h-10 flex items-center justify-center rounded-md border border-[#CBD5E1] bg-white text-[#475569] hover:bg-[#F1F5F9] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 active:scale-95" aria-label="Decrease quantity" data-id="element-5380" data-mp="2aqu5a2yxez8u">
                            <Minus className="w-4 h-4" data-id="element-5381" data-mp="la88dok0mejn" />
                          </button>
                          <span className="w-10 text-center text-[14px] font-semibold text-[#0F172A]" data-id="element-5382" data-mp="3tpryiepe3guh">
                            {quantity}
                          </span>
                          <button onClick={() => updateProductQuantity(product.id, 1)} className="w-10 h-10 flex items-center justify-center rounded-md border border-[#0EA5E9] bg-[#0EA5E9] text-white hover:bg-[#0284C7] hover:border-[#0284C7] transition-all duration-200 active:scale-95 shadow-sm" aria-label="Increase quantity" data-id="element-5383" data-mp="7i1uk94c4jnd">
                            <Plus className="w-4 h-4" data-id="element-5384" data-mp="1dwszcnwkwomo" />
                          </button>
                        </div>
                      </div>;
            })}
              </div>
            </div>
          </div> :
      // View Mode - Table Layout
      <div data-id="element-5385" data-mp="30rxxu91wv9os">
            {/* Table Header */}
            <div className="bg-[#F1F5F9] border-b border-[#E2E8F0] rounded-t-md" data-id="element-5386" data-mp="2mvkppqj9okmz">
              <div className="grid grid-cols-12 gap-4 px-4 py-3" data-id="element-5387" data-mp="2rt46oy7cnbim">
                <div className="col-span-6" data-id="element-5388" data-mp="2rvgtpprysc2j">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-5389" data-mp="3fk9kl7tqbtqa">
                    Item
                  </span>
                </div>
                <div className="col-span-2 text-right" data-id="element-5390" data-mp="35tx1lk1kc8m9">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-5391" data-mp="35t576cjpcgxk">
                    Price
                  </span>
                </div>
                <div className="col-span-2 text-center" data-id="element-5392" data-mp="2bwqvx4mhp4y">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-5393" data-mp="73512vds1ej6">
                    Qty
                  </span>
                </div>
                <div className="col-span-2 text-right" data-id="element-5394" data-mp="nf8idoqm45p5">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-5395" data-mp="sant47tbplhc">
                    Total
                  </span>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div data-id="element-5396" data-mp="ijt6v3g2ulvw">
              {subscriptionItems.map((item, index) => {
            const price = getProductPrice(item.name);
            const itemTotal = price * item.quantity;
            return <div key={index} className={`grid grid-cols-12 gap-4 px-4 py-4 ${index !== subscriptionItems.length - 1 ? 'border-b border-[#E2E8F0]' : ''} hover:bg-[#F8FAFC] transition-colors duration-200`} data-id="element-5397" data-mp="9jeke0rdn0qt" data-mpi={index}>
                    <div className="col-span-6 flex items-center" data-id="element-5398" data-mp="3vuipxcd9u34b">
                      <p className="text-[14px] text-[#0F172A] font-medium" data-id="element-5399" data-mp="1q08zooudpl0m">
                        {item.name}
                      </p>
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end" data-id="element-5400" data-mp="3eilmux930gkr">
                      <span className="text-[14px] text-[#475569]" data-id="element-5401" data-mp="3aibh9lp7bs6f">
                        ${price.toFixed(2)}
                      </span>
                    </div>
                    <div className="col-span-2 text-center flex items-center justify-center" data-id="element-5402" data-mp="3ob14ftggv354">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 bg-[#F1F5F9] text-[#0F172A] font-semibold rounded-md text-[14px]" data-id="element-5403" data-mp="3jgddzp1wjynw">
                        {item.quantity}
                      </span>
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end" data-id="element-5404" data-mp="b1d7ck1ttst7">
                      <span className="text-[14px] text-[#0F172A] font-semibold" data-id="element-5405" data-mp="64mrnxtxoz8b">
                        ${itemTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>;
          })}
            </div>

            {/* Totals Section */}
            <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-5406" data-mp="bs1vnyig7n8i">
              <div className="flex justify-end" data-id="element-5407" data-mp="btwi5h9dbnep">
                <div className="w-80 space-y-3" data-id="element-5408" data-mp="gq3ecibypkjg">
                  <div className="flex justify-between items-center" data-id="element-5409" data-mp="gszca6nt897w">
                    <span className="text-[14px] text-[#475569]" data-id="element-5410" data-mp="lp6lc8bi60u2">Subtotal</span>
                    <span className="text-[14px] text-[#0F172A] font-medium" data-id="element-5411" data-mp="prtcs849hjqq">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-5412" data-mp="zphan6x5z7d4">
                    <span className="text-[14px] text-[#10B981] font-medium" data-id="element-5413" data-mp="zno19jv6291f">
                      Savings ({discountPercent}%)
                    </span>
                    <span className="text-[14px] text-[#10B981] font-semibold" data-id="element-5414" data-mp="19eipgtrz3ipi">
                      -${discountAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-[#CBD5E1]" data-id="element-5415" data-mp="1ifpbfide5lq5">
                    <span className="text-[16px] text-[#0F172A] font-semibold" data-id="element-5416" data-mp="32yssdmfvaqxu">
                      Total
                    </span>
                    <span className="text-[16px] text-[#0EA5E9] font-semibold" data-id="element-5417" data-mp="2xxmxbhyqirl5">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </Card>;
}